import {
    afterNextRender,
    DestroyRef,
    Directive,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    untracked,
} from '@angular/core';

const DURATION_MS = 320;
const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Troca de tamanho da mídia (Grande, Média, Pequena, largura livre) sem pulo.
 *
 * O CSS continua dono do tamanho: aqui só se anima a passagem. Quando a chave `sizeMorph`
 * muda, o elemento já está no tamanho novo; ele é medido e animado da largura antiga para a
 * nova, e a mídia de dentro (`sizeMorphMedia`, um seletor) da altura antiga para a nova. A
 * altura precisa da própria animação porque cada tamanho tem a sua proporção
 * (`aspect-ratio`), e a troca de proporção seria instantânea.
 *
 * O arrasto dos cantos muda a largura livre a cada movimento: nele a chave não muda (a chave
 * é o preset, e a largura livre só entra quando o arrasto termina), então a foto segue o
 * mouse sem animação.
 */
@Directive({
    selector: '[sizeMorph]',
})
export class SizeMorphDirective {
    /** Chave do tamanho: a animação roda só quando ela muda. */
    public sizeMorph = input<string | number | null>(null);
    /** A mídia de dentro cuja altura acompanha (ex.: 'img', 'video'). */
    public sizeMorphMedia = input<string>('img');

    private _el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private _injector = inject(Injector);
    /** Tamanho visto por último: é o "de antes" da próxima troca. */
    private _last = { width: 0, height: 0 };
    private _lastKey: string | number | null | undefined = undefined;
    private _running: Animation[] = [];

    constructor() {
        const observer =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(() => {
                      if (!this._running.length) this._remember();
                  });
        observer?.observe(this._el);

        effect(onCleanup => {
            const key = this.sizeMorph();
            untracked(() => {
                const first = this._lastKey === undefined;
                if (key === this._lastKey) return;
                this._lastKey = key;
                if (first || prefersReducedMotion()) return;

                // Trocou no meio de outra troca: parte de onde está NA TELA agora (o efeito
                // roda antes da tela mudar), não do fim da animação anterior.
                const from = this._running.length
                    ? { width: this._el.offsetWidth, height: this._media()?.offsetHeight ?? 0 }
                    : this._last.width
                      ? { ...this._last }
                      : null;
                const ref = afterNextRender(() => this._morph(from), { injector: this._injector });
                onCleanup(() => ref.destroy());
            });
        });

        inject(DestroyRef).onDestroy(() => {
            observer?.disconnect();
            this._cancel();
        });
    }

    private _media(): HTMLElement | null {
        return this._el.querySelector<HTMLElement>(this.sizeMorphMedia());
    }

    private _remember(): void {
        this._last = {
            width: this._el.offsetWidth,
            height: this._media()?.offsetHeight ?? 0,
        };
    }

    private _morph(from: { width: number; height: number } | null): void {
        // Uma troca no meio de outra: parte de onde a anterior estava na tela.
        this._cancel();
        const media = this._media();
        if (!from) {
            this._remember();
            return;
        }

        const to = { width: this._el.offsetWidth, height: media?.offsetHeight ?? 0 };
        const options: KeyframeAnimationOptions = { duration: DURATION_MS, easing: EASING };

        if (Math.abs(from.width - to.width) >= 1) {
            this._running.push(
                this._el.animate([{ width: `${from.width}px` }, { width: `${to.width}px` }], options)
            );
        }
        if (media && from.height && to.height && Math.abs(from.height - to.height) >= 1) {
            this._running.push(
                media.animate([{ height: `${from.height}px` }, { height: `${to.height}px` }], options)
            );
        }

        if (!this._running.length) {
            this._remember();
            return;
        }

        const running = this._running;
        Promise.allSettled(running.map(animation => animation.finished)).then(() => {
            if (this._running !== running) return;
            this._running = [];
            this._remember();
        });
    }

    private _cancel(): void {
        this._running.forEach(animation => animation.cancel());
        this._running = [];
    }
}

function prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
